/**
 * Add Vehicle Page - Dynamic Form
 * ================================
 * صفحة إضافة مركبة بحقول ديناميكية من قاعدة البيانات
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';

const I = {
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>,
  truck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>,
  upload: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  image: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
};

const AddVehiclePage = () => {
  const navigate = useNavigate();
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [ships, setShips] = useState([]);
  const [requirementGroups, setRequirementGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(0);
  
  // Form data
  const [vehicleCode, setVehicleCode] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [shipId, setShipId] = useState('');
  const [formData, setFormData] = useState({});
  const [images, setImages] = useState([]);
  const [profileImageIndex, setProfileImageIndex] = useState(0);
  const [errors, setErrors] = useState({});

  // Load categories, ships and requirements
  const loadData = useCallback(async () => {
    try {
      const [catsRes, shipsRes, reqsRes] = await Promise.all([
        apiCall('/tenant/vehicle-settings/categories'),
        apiCall('/tenant/ships'),
        apiCall('/tenant/vehicle-settings/requirements/grouped')
      ]);
      setCategories(catsRes);
      setShips(shipsRes.items || shipsRes);
      setRequirementGroups(reqsRes);
      
      // Initialize form data with defaults
      const initialData = {};
      reqsRes.forEach(group => {
        group.fields.forEach(field => {
          initialData[field.field_key] = field.default_value || '';
        });
      });
      setFormData(initialData);
    } catch (err) {
      showError('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  useEffect(() => { loadData(); }, [loadData]);

  // Validation
  const validateField = (field, value) => {
    if (field.is_required && (!value || value.trim() === '')) {
      return `${field.field_name_ar} مطلوب`;
    }
    
    if (value && field.validation_regex) {
      const regex = new RegExp(field.validation_regex);
      if (!regex.test(value)) {
        return field.validation_message || `${field.field_name_ar} غير صالح`;
      }
    }
    
    if (value && field.min_length && value.length < field.min_length) {
      return `${field.field_name_ar} يجب أن يكون ${field.min_length} أحرف على الأقل`;
    }
    
    if (value && field.max_length && value.length > field.max_length) {
      return `${field.field_name_ar} يجب أن لا يتجاوز ${field.max_length} أحرف`;
    }
    
    return null;
  };

  const validateAll = () => {
    const newErrors = {};
    let isValid = true;
    
    if (!categoryId) {
      newErrors.category = 'يجب اختيار فئة المركبة';
      isValid = false;
    }
    
    if (!shipId) {
      newErrors.ship = 'يجب اختيار الشيب';
      isValid = false;
    }
    
    requirementGroups.forEach(group => {
      group.fields.forEach(field => {
        const error = validateField(field, formData[field.field_key]);
        if (error) {
          newErrors[field.field_key] = error;
          isValid = false;
        }
      });
    });
    
    setErrors(newErrors);
    return isValid;
  };

  // Handle input change
  const handleInputChange = (fieldKey, value, field) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }));
    
    // Clear error on change
    if (errors[fieldKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  // Saudi Plate Letters Mapping (Arabic <-> English)
  const saudiLettersMap = {
    'أ': 'A', 'ب': 'B', 'ح': 'J', 'د': 'D', 'ر': 'R', 'س': 'S', 'ص': 'X', 'ط': 'T',
    'ع': 'E', 'ق': 'G', 'ك': 'K', 'ل': 'L', 'م': 'Z', 'ن': 'N', 'ه': 'H', 'و': 'U', 'ى': 'V'
  };
  const validArabicLetters = Object.keys(saudiLettersMap);

  // Convert English numbers to Arabic
  const convertToArabicNumbers = (str) => {
    const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return str.replace(/[0-9]/g, d => arabicDigits[parseInt(d)]);
  };

  // Convert Arabic numbers to English
  const convertToEnglishNumbers = (str) => {
    const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return str.replace(/[٠-٩]/g, d => arabicDigits.indexOf(d).toString());
  };

  // Convert Arabic letters to English (Saudi plate)
  const convertArabicLettersToEnglish = (arabicLetters) => {
    return arabicLetters.split(' ').map(l => saudiLettersMap[l] || '').join(' ');
  };

  // Handle plate number change (syncs Arabic and English)
  const handlePlateNumberChange = (value) => {
    // Allow only digits, max 4
    let digits = value.replace(/[^0-9٠-٩]/g, '');
    // Convert any Arabic digits to English first
    digits = convertToEnglishNumbers(digits);
    // Limit to 4 digits
    digits = digits.slice(0, 4);
    
    // Set Arabic version
    const arabicNum = convertToArabicNumbers(digits);
    // Set English version
    const englishNum = digits;
    
    setFormData(prev => ({
      ...prev,
      plate_number_ar: arabicNum,
      plate_number_en: englishNum
    }));
  };

  // Handle plate letters change (syncs Arabic and English)
  const handlePlateLettersChange = (value) => {
    // Filter only valid Saudi Arabic letters
    const filtered = value.split('').filter(c => validArabicLetters.includes(c) || c === ' ');
    // Get unique letters (max 3)
    const letters = [];
    for (const c of filtered) {
      if (c !== ' ' && letters.length < 3 && !letters.includes(c)) {
        letters.push(c);
      }
    }
    
    const arabicLetters = letters.join(' ');
    const englishLetters = convertArabicLettersToEnglish(arabicLetters);
    
    setFormData(prev => ({
      ...prev,
      plate_letters_ar: arabicLetters,
      plate_letters_en: englishLetters
    }));
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 4) {
      showError('الحد الأقصى 4 صور');
      return;
    }
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImages(prev => [...prev, { file, preview: ev.target.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    if (profileImageIndex === index) setProfileImageIndex(0);
    else if (profileImageIndex > index) setProfileImageIndex(prev => prev - 1);
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateAll()) {
      showError('يرجى تصحيح الأخطاء');
      return;
    }
    
    setSubmitting(true);
    try {
      // Filter out File objects from formData
      const cleanFormData = Object.fromEntries(
        Object.entries(formData).filter(([k, v]) => !(v instanceof File) && !k.endsWith('_file'))
      );
      
      // Create vehicle
      const result = await apiCall('/tenant/vehicles/dynamic', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_code: vehicleCode || null,
          category_id: categoryId,
          ship_id: shipId,
          data: cleanFormData
        })
      });
      
      // Upload images if any
      if (images.length > 0) {
        const token = localStorage.getItem('watrly_tenant_token');
        for (let i = 0; i < images.length; i++) {
          const uploadForm = new FormData();
          uploadForm.append('file', images[i].file);
          uploadForm.append('vehicle_id', result.id);
          uploadForm.append('document_type', `vehicle_image_${i}`);
          
          await fetch('http://localhost:8002/api/v1/tenant/uploads/vehicle-document', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: uploadForm
          });
        }
      }
      
      // Upload file fields
      const filesToUpload = Object.entries(formData).filter(([k, v]) => k.endsWith('_file') && v instanceof File);
      if (filesToUpload.length > 0) {
        const token = localStorage.getItem('watrly_tenant_token');
        for (const [key, file] of filesToUpload) {
          const fieldKey = key.replace('_file', '');
          const uploadForm = new FormData();
          uploadForm.append('file', file);
          uploadForm.append('vehicle_id', result.id);
          uploadForm.append('document_type', fieldKey);
          
          await fetch('http://localhost:8002/api/v1/tenant/uploads/vehicle-document', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: uploadForm
          });
        }
      }
      
      success('تم إضافة المركبة بنجاح');
      navigate(`/tenant/vehicles/${result.id}`);
    } catch (err) {
      showError(err.message || 'فشل إضافة المركبة');
    } finally {
      setSubmitting(false);
    }
  };

  // Render field based on type
  const renderField = (field) => {
    const value = formData[field.field_key] || '';
    const error = errors[field.field_key];
    
    const commonProps = {
      value,
      placeholder: field.placeholder || '',
      disabled: submitting,
      className: error ? 'input-error' : ''
    };

    switch (field.field_type) {
      case 'text':
        return (
          <input 
            type="text" 
            {...commonProps}
            onChange={(e) => handleInputChange(field.field_key, e.target.value, field)}
          />
        );
      
      case 'number':
        // For English plate number - readonly, synced from Arabic
        if (field.field_key === 'plate_number_en') {
          return (
            <input 
              type="text" 
              dir="ltr"
              value={value}
              placeholder="يُملأ تلقائياً"
              disabled={true}
              readOnly
              className="auto-filled"
            />
          );
        }
        return (
          <input 
            type="text" 
            dir="ltr"
            {...commonProps}
            onChange={(e) => {
              let val = e.target.value.replace(/[^0-9]/g, '');
              handleInputChange(field.field_key, val, field);
            }}
          />
        );
      
      case 'number_ar':
        // Plate number Arabic - syncs with English automatically
        if (field.field_key === 'plate_number_ar') {
          return (
            <div className="plate-input-group">
              <input 
                type="text" 
                dir="rtl"
                value={value}
                placeholder="أدخل 1-4 أرقام"
                disabled={submitting}
                className={error ? 'input-error' : ''}
                onChange={(e) => handlePlateNumberChange(e.target.value)}
                maxLength={4}
              />
              <span className="auto-sync">← {formData.plate_number_en || '...'}</span>
            </div>
          );
        }
        return (
          <input 
            type="text" 
            dir="rtl"
            {...commonProps}
            onChange={(e) => {
              let val = e.target.value.replace(/[^0-9٠-٩]/g, '');
              val = convertToArabicNumbers(val);
              val = val.slice(0, 4);
              handleInputChange(field.field_key, val, field);
            }}
            maxLength={4}
          />
        );
      
      case 'letters_ar':
        // Plate letters Arabic - syncs with English automatically
        if (field.field_key === 'plate_letters_ar') {
          return (
            <div className="plate-input-group">
              <input 
                type="text" 
                value={value}
                placeholder="مثال: أ ب ج"
                disabled={submitting}
                className={error ? 'input-error' : ''}
                onChange={(e) => handlePlateLettersChange(e.target.value)}
                maxLength={5}
              />
              <span className="auto-sync">← {formData.plate_letters_en || '...'}</span>
            </div>
          );
        }
        return (
          <input 
            type="text" 
            {...commonProps}
            onChange={(e) => {
              let val = e.target.value.replace(/[^\u0621-\u064A\s]/g, '');
              val = val.replace(/\s+/g, ' ').trim();
              const letters = val.split(' ').filter(l => l).slice(0, 3);
              handleInputChange(field.field_key, letters.join(' '), field);
            }}
            maxLength={5}
          />
        );
      
      case 'letters_en':
        // English plate letters - readonly, synced from Arabic
        if (field.field_key === 'plate_letters_en') {
          return (
            <input 
              type="text" 
              dir="ltr"
              value={value}
              placeholder="يُملأ تلقائياً"
              disabled={true}
              readOnly
              className="auto-filled"
            />
          );
        }
        return (
          <input 
            type="text" 
            dir="ltr"
            {...commonProps}
            onChange={(e) => {
              let val = e.target.value.toUpperCase().replace(/[^A-Z\s]/g, '');
              val = val.replace(/\s+/g, ' ').trim();
              const letters = val.split(' ').filter(l => l).slice(0, 3);
              handleInputChange(field.field_key, letters.join(' '), field);
            }}
            maxLength={5}
          />
        );
      
      case 'select':
        const options = field.options || [];
        return (
          <select 
            value={value}
            onChange={(e) => handleInputChange(field.field_key, e.target.value, field)}
            disabled={submitting}
          >
            <option value="">اختر</option>
            {options.map(opt => (
              <option key={opt} value={opt}>
                {opt === 'diesel' ? 'ديزل' : opt === 'petrol' ? 'بنزين' : opt === 'hybrid' ? 'هايبرد' : opt === 'electric' ? 'كهربائي' : opt}
              </option>
            ))}
          </select>
        );
      
      case 'date':
        return (
          <input 
            type="date" 
            {...commonProps}
            onChange={(e) => handleInputChange(field.field_key, e.target.value, field)}
          />
        );
      
      case 'textarea':
        return (
          <textarea 
            {...commonProps}
            rows={3}
            onChange={(e) => handleInputChange(field.field_key, e.target.value, field)}
          />
        );
      
      case 'file':
        return (
          <div className="file-upload">
            <input 
              type="file"
              id={`file-${field.field_key}`}
              accept={field.file_types ? field.file_types.split(',').map(t => `.${t}`).join(',') : '*'}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  // Store file for upload after vehicle creation
                  setFormData(prev => ({ ...prev, [`${field.field_key}_file`]: file }));
                  handleInputChange(field.field_key, file.name, field);
                }
              }}
              disabled={submitting}
            />
            <label htmlFor={`file-${field.field_key}`} className="file-label">
              {I.upload}
              <span>{value || 'اختر ملف'}</span>
            </label>
            {field.file_types && <small>الأنواع المسموحة: {field.file_types}</small>}
          </div>
        );
      
      default:
        return (
          <input 
            type="text" 
            {...commonProps}
            onChange={(e) => handleInputChange(field.field_key, e.target.value, field)}
          />
        );
    }
  };

  if (loading) {
    return <div className="page-loading"><div className="spinner" /></div>;
  }

  return (
    <div className="add-vehicle-page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/tenant/vehicles')}>{I.back}</button>
        <div className="page-header__title">
          <h1>إضافة مركبة جديدة</h1>
          <p>أدخل بيانات المركبة</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="vehicle-form">
        {/* Category & Code Section */}
        <div className="form-section">
          <h3>معلومات أساسية</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>فئة المركبة *</label>
              <select 
                value={categoryId} 
                onChange={(e) => setCategoryId(e.target.value)}
                className={errors.category ? 'input-error' : ''}
                required
              >
                <option value="">اختر الفئة</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name} {cat.capacity_tons ? `(${cat.capacity_tons} طن)` : ''}
                  </option>
                ))}
              </select>
              {errors.category && <span className="error-text">{errors.category}</span>}
            </div>
            <div className="form-group">
              <label>الشيب (صاحب المنشأة) *</label>
              <select 
                value={shipId} 
                onChange={(e) => setShipId(e.target.value)}
                className={errors.ship ? 'input-error' : ''}
                required
              >
                <option value="">اختر الشيب</option>
                {ships.map(ship => (
                  <option key={ship.id} value={ship.id}>
                    {ship.name || ship.company_name || `شيب #${ship.id.slice(0,8)}`}
                  </option>
                ))}
              </select>
              {errors.ship && <span className="error-text">{errors.ship}</span>}
            </div>
            <div className="form-group">
              <label>رمز المركبة</label>
              <input 
                type="text" 
                value={vehicleCode}
                onChange={(e) => setVehicleCode(e.target.value)}
                placeholder="اتركه فارغ للإنشاء التلقائي"
              />
              <small>سيتم إنشاء رمز تلقائي إذا تُرك فارغاً</small>
            </div>
          </div>
        </div>

        {/* Images Section */}
        <div className="form-section">
          <h3>صور المركبة</h3>
          <p className="section-desc">أضف حتى 4 صور واختر الصورة الرئيسية للعرض</p>
          
          <div className="images-grid">
            {images.map((img, idx) => (
              <div key={idx} className={`image-item ${idx === profileImageIndex ? 'profile' : ''}`}>
                <img src={img.preview} alt={`صورة ${idx + 1}`} />
                <div className="image-actions">
                  <button type="button" onClick={() => setProfileImageIndex(idx)} title="تعيين كصورة رئيسية">
                    {I.check}
                  </button>
                  <button type="button" onClick={() => removeImage(idx)} title="حذف">
                    {I.x}
                  </button>
                </div>
                {idx === profileImageIndex && <span className="profile-badge">رئيسية</span>}
              </div>
            ))}
            
            {images.length < 4 && (
              <label className="image-upload">
                <input type="file" accept="image/*" onChange={handleImageUpload} multiple />
                {I.image}
                <span>إضافة صورة</span>
              </label>
            )}
          </div>
        </div>

        {/* Dynamic Fields by Group */}
        <div className="form-tabs">
          {requirementGroups.map((group, idx) => (
            <button 
              key={group.key}
              type="button"
              className={`form-tab ${activeGroup === idx ? 'active' : ''}`}
              onClick={() => setActiveGroup(idx)}
            >
              {group.label}
              {group.fields.some(f => errors[f.field_key]) && <span className="tab-error">!</span>}
            </button>
          ))}
        </div>

        {requirementGroups.map((group, idx) => (
          <div key={group.key} className={`form-section ${activeGroup === idx ? '' : 'hidden'}`}>
            <div className="form-grid">
              {group.fields.map(field => (
                <div key={field.field_key} className={`form-group ${field.field_type === 'textarea' || field.field_type === 'file' ? 'full-width' : ''}`}>
                  <label>
                    {field.field_name_ar}
                    {field.is_required && <span className="required">*</span>}
                    {field.needs_verification && <span className="verify-badge">يحتاج مراجعة</span>}
                  </label>
                  {renderField(field)}
                  {errors[field.field_key] && <span className="error-text">{errors[field.field_key]}</span>}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Submit */}
        <div className="form-actions">
          <button type="button" className="btn btn--secondary" onClick={() => navigate('/tenant/vehicles')}>
            إلغاء
          </button>
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? 'جاري الحفظ...' : 'حفظ المركبة'}
          </button>
        </div>
      </form>

      <style>{`
        .add-vehicle-page { padding: 1.5rem; max-width: 900px; margin: 0 auto; }
        .page-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 2rem; }
        .back-btn { background: var(--tenant-bg-secondary); border: none; padding: 0.5rem; border-radius: 8px; cursor: pointer; display: flex; }
        .back-btn svg { width: 20px; height: 20px; color: var(--tenant-text); }
        .page-header__title h1 { margin: 0; font-size: 1.5rem; }
        .page-header__title p { margin: 0.25rem 0 0; color: var(--tenant-text-secondary); }
        
        .vehicle-form { display: flex; flex-direction: column; gap: 1.5rem; }
        
        .form-section { background: var(--tenant-bg-secondary); border-radius: 12px; padding: 1.5rem; }
        .form-section.hidden { display: none; }
        .form-section h3 { margin: 0 0 1rem; font-size: 1.1rem; color: var(--tenant-text); }
        .section-desc { margin: -0.5rem 0 1rem; color: var(--tenant-text-secondary); font-size: 0.875rem; }
        
        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; }
        .form-group { display: flex; flex-direction: column; gap: 0.5rem; }
        .form-group.full-width { grid-column: span 2; }
        .form-group label { font-weight: 500; color: var(--tenant-text); display: flex; align-items: center; gap: 0.5rem; }
        .required { color: var(--tenant-danger); }
        .verify-badge { font-size: 0.7rem; background: var(--tenant-warning); color: #000; padding: 2px 6px; border-radius: 4px; font-weight: normal; }
        
        .form-group input, .form-group select, .form-group textarea {
          padding: 0.75rem; border: 1px solid var(--tenant-border); border-radius: 8px;
          background: var(--tenant-bg); color: var(--tenant-text); font-size: 1rem;
        }
        .form-group input:focus, .form-group select:focus, .form-group textarea:focus {
          outline: none; border-color: var(--tenant-primary);
        }
        .form-group input.input-error, .form-group select.input-error {
          border-color: var(--tenant-danger);
        }
        .form-group small { color: var(--tenant-text-secondary); font-size: 0.75rem; }
        .error-text { color: var(--tenant-danger); font-size: 0.75rem; }
        
        .plate-input-group { display: flex; align-items: center; gap: 0.75rem; }
        .plate-input-group input { flex: 1; }
        .auto-sync { font-size: 0.875rem; color: var(--tenant-primary); font-weight: 600; 
          background: var(--tenant-bg); padding: 0.5rem 0.75rem; border-radius: 6px;
          border: 1px solid var(--tenant-primary); min-width: 80px; text-align: center; direction: ltr; }
        .auto-filled { background: var(--tenant-bg-secondary) !important; color: var(--tenant-text-secondary) !important;
          cursor: not-allowed; }
        
        .file-upload { position: relative; }
        .file-upload input[type="file"] { display: none; }
        .file-label { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem 1rem;
          border: 1px dashed var(--tenant-border); border-radius: 8px; cursor: pointer;
          background: var(--tenant-bg); transition: all 0.2s; }
        .file-label:hover { border-color: var(--tenant-primary); }
        .file-label svg { width: 20px; height: 20px; color: var(--tenant-text-secondary); }
        
        .images-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; }
        .image-item { position: relative; aspect-ratio: 1; border-radius: 8px; overflow: hidden; border: 2px solid transparent; }
        .image-item.profile { border-color: var(--tenant-primary); }
        .image-item img { width: 100%; height: 100%; object-fit: cover; }
        .image-actions { position: absolute; top: 0.5rem; right: 0.5rem; display: flex; gap: 0.25rem; }
        .image-actions button { width: 28px; height: 28px; border-radius: 50%; border: none;
          background: rgba(0,0,0,0.6); color: white; cursor: pointer; display: flex;
          align-items: center; justify-content: center; }
        .image-actions button svg { width: 14px; height: 14px; }
        .profile-badge { position: absolute; bottom: 0; left: 0; right: 0; background: var(--tenant-primary);
          color: white; text-align: center; padding: 4px; font-size: 0.75rem; }
        .image-upload { aspect-ratio: 1; border: 2px dashed var(--tenant-border); border-radius: 8px;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 0.5rem; cursor: pointer; transition: all 0.2s; color: var(--tenant-text-secondary); }
        .image-upload:hover { border-color: var(--tenant-primary); color: var(--tenant-primary); }
        .image-upload input { display: none; }
        .image-upload svg { width: 32px; height: 32px; }
        
        .form-tabs { display: flex; gap: 0.5rem; flex-wrap: wrap; }
        .form-tab { padding: 0.75rem 1.25rem; border: none; background: var(--tenant-bg-secondary);
          color: var(--tenant-text-secondary); border-radius: 8px; cursor: pointer;
          transition: all 0.2s; position: relative; }
        .form-tab:hover { background: var(--tenant-bg); }
        .form-tab.active { background: var(--tenant-primary); color: white; }
        .tab-error { position: absolute; top: -4px; right: -4px; width: 16px; height: 16px;
          background: var(--tenant-danger); color: white; border-radius: 50%;
          font-size: 0.7rem; display: flex; align-items: center; justify-content: center; }
        
        .form-actions { display: flex; justify-content: flex-end; gap: 1rem; padding-top: 1rem; }
        
        @media (max-width: 768px) {
          .form-grid { grid-template-columns: 1fr; }
          .form-group.full-width { grid-column: span 1; }
          .images-grid { grid-template-columns: repeat(2, 1fr); }
        }
      `}</style>
    </div>
  );
};

export default AddVehiclePage;
